import { useSidebar } from "../contexts/SidebarContext";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { Outlet } from "react-router-dom";
import SearchBarResponsive from "../components/Product/SearchBarResponsive";
import { NotificationProvider } from "@/contexts/NotificationsContext";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef } from "react";
import { addNewOrder } from "@/store/ordersSlice";
import MobileAppNotificationWarning from "@/components/MobileAppNotificationWarning";
import { Capacitor } from "@capacitor/core";
import { PushPermissionProvider } from "@/contexts/PushPermissionContext";
import { RootState } from "@/store/store";
import { useUserRole } from "@/hooks/useUserRole";
import SubscriptionWarning from "@/components/SubscriptionWarning";
import { setOrderProgress } from "@/store/orderProgressUpdate";
import { useBrowserFinishedListener } from "@/hooks/useBrowserFinishedListener";
import { isWeb } from "@/utils/helpers";

const AppLayout = () => {
  const { showSidebarOverlay, handleToggleSidebar, isShowingIconsOnly } =
    useSidebar();
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const dispatch = useDispatch();
  const base_socket = import.meta.env.VITE_SOCKET;
  const manuallyClosed = useRef(false);
  const { isAdmin, isBranchAdmin, isCashier } = useUserRole()
  const shouldSeeSearchbar = isAdmin || isBranchAdmin || isCashier
  const user = useSelector((state: RootState) => state.user.userData);




  useEffect(() => {
    if (user?.role === "chef") return;
    if (!token || !base_socket) return;
    let socket: WebSocket | null = null;
    let reconnectTimeout: NodeJS.Timeout | null = null;

    const connectWebSocket = () => {
      try {
        socket = new WebSocket(`${base_socket}/socket/dashboard/orders`);

        socket.addEventListener("open", () => {
          try {
            socket?.send(
              JSON.stringify({
                type: "authorize",
                token,
              })
            );
          } catch {
            // Silent fail - sending auth message failed
          }
        });

        socket.addEventListener("message", (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.order) {
              dispatch(addNewOrder(data.order));
            } else if (data.messageType === "order-update") {
              dispatch(setOrderProgress(data));
            }
          } catch {
            // Silent fail - invalid JSON or unexpected message
          }
        });

        socket.addEventListener("close", () => {
          if (!manuallyClosed.current) {
            reconnectTimeout = setTimeout(connectWebSocket, 5000);
          }
        });

        socket.addEventListener("error", () => {
          try {
            socket?.close(); // Will trigger `close` and schedule reconnect
          } catch {
            // Silent fail
          }
        });
      } catch {
        reconnectTimeout = setTimeout(connectWebSocket, 5000);
      }
    };

    connectWebSocket();

    return () => {
      manuallyClosed.current = true;
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.close();
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }, [dispatch, token, base_socket, user?.role]);

  useBrowserFinishedListener()

  return (
    <PushPermissionProvider>
      <NotificationProvider>
        <div
          className={`grid h-dvh w-dvw ${isShowingIconsOnly ? "grid-cols-[80px_repeat(11,1fr)]" : "grid-cols-[260px_repeat(11,1fr)]"} grid-rows-12 overflow-hidden transition-[grid] duration-200 ${!isWeb ? "pt-6 md:pt-0 bg-sidebar md:bg-transparent": ""} `}
        >
          <Header />


          <Sidebar />

          <main
            id="noScroll"
            className={`thin-scrollbar col-start-1 col-end-13 row-start-2 row-end-13 bg-body p-4 md:col-start-2 lg:p-10 overflow-y-auto overflow-x-hidden`}
          >
            {Capacitor.getPlatform() !== "web" && (
              <MobileAppNotificationWarning />
            )}
            {shouldSeeSearchbar && <SearchBarResponsive />}
            <SubscriptionWarning />
            <Outlet />
          </main>

          {/* sidebar OverLay */}
          {showSidebarOverlay && (
            <div
              onClick={handleToggleSidebar}
              className="z-[999] col-start-1 col-end-13 row-start-2 row-end-13 bg-black/20"
            ></div>
          )}
        </div>
      </NotificationProvider>
    </PushPermissionProvider>
  );
};

export default AppLayout;
